import { useEffect, useMemo, useRef } from 'react'

import type { SessionResponse } from '@/api/types'
import { SmallCapsLabel, StatusDot } from '@/ui/primitives'

import type { LiveFeedEntry } from './transcript'

interface Props {
  items: LiveFeedEntry[]
  session: SessionResponse | null
  streaming: boolean
}

function formatStamp(value: string): string {
  return new Date(value).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

function toneClass(tone: LiveFeedEntry['tone']): string {
  switch (tone) {
    case 'accent':
      return 'text-accent'
    case 'warn':
      return 'text-warn'
    case 'danger':
      return 'text-danger'
    case 'info':
      return 'text-info'
    case 'dim':
      return 'text-fg-dim'
    default:
      return 'text-fg'
  }
}

function dotTone(tone: LiveFeedEntry['tone']): 'accent' | 'warn' | 'danger' | 'info' | 'dim' | 'fg' {
  return tone === 'info' ? 'info' : tone
}

export function LiveFeed({ items, session, streaming }: Props) {
  const scrollerRef = useRef<HTMLDivElement | null>(null)
  const stickToBottomRef = useRef(true)

  useEffect(() => {
    const node = scrollerRef.current
    if (!node) return
    const onScroll = () => {
      const distance = node.scrollHeight - node.scrollTop - node.clientHeight
      stickToBottomRef.current = distance < 80
    }
    onScroll()
    node.addEventListener('scroll', onScroll)
    return () => node.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    const node = scrollerRef.current
    if (!node || !stickToBottomRef.current) return
    node.scrollTop = node.scrollHeight
  }, [items.length])

  const headerMeta = useMemo(() => {
    if (!session) return 'no session attached'
    return `${items.length} event${items.length === 1 ? '' : 's'}`
  }, [items.length, session])

  return (
    <div className="h-full min-h-0 flex flex-col bg-bg-raise">
      <div className="shrink-0 border-b border-line bg-bg-raise px-5 py-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <SmallCapsLabel tone="dim">live feed</SmallCapsLabel>
            <div className="mt-1 pr-2 font-mono text-[14px] text-fg feed-wrap">
              {session ? session.title || '(untitled)' : 'waiting for session'}
            </div>
            <div className="mt-1 font-mono text-[12px] text-fg-dim">{headerMeta}</div>
          </div>
          <div className="inline-flex shrink-0 items-center gap-2 border border-line bg-surface px-2 py-1 font-mono text-[11.5px] text-fg-dim">
            <StatusDot tone={streaming ? 'accent' : 'dim'} pulse={streaming} size={7} />
            <span>{streaming ? 'live' : 'idle'}</span>
          </div>
        </div>
      </div>

      <div ref={scrollerRef} className="flex-1 min-h-0 overflow-y-auto px-2 py-2">
        {items.length === 0 ? (
          <div className="panel-inset px-4 py-4 font-mono text-[12.5px] text-fg-dim">
            {session
              ? '// no events recorded yet for this session.'
              : '// open a session to watch the event feed.'}
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => {
              const hasDetail = Boolean(item.detail && item.detail !== item.summary)
              return (
                <div
                  key={item.id}
                  className="panel-inset px-2 py-2 font-mono animate-fade-in transition-colors hover:border-line-strong"
                >
                  <div className="flex items-start gap-3">
                    <StatusDot
                      tone={dotTone(item.tone)}
                      pulse={streaming && item === items[items.length - 1]}
                      size={7}
                      className="mt-1 shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] uppercase tracking-[0.08em]">
                        <span className={toneClass(item.tone)}>{item.title}</span>
                        <span className="text-fg-dim">·</span>
                        <span className="text-fg-dim normal-case tracking-normal">
                          {formatStamp(item.ts)}
                        </span>
                      </div>
                      <div className="mt-1 pr-1 text-[13px] leading-[1.6] text-fg whitespace-pre-wrap feed-wrap">
                        {item.summary}
                      </div>
                      {hasDetail && (
                        <details className="mt-2 border-t border-line pt-2">
                          <summary className="cursor-pointer text-[11.5px] uppercase tracking-[0.08em] text-fg-dim hover:text-fg">
                            details
                          </summary>
                          <pre className="mt-2 pr-1 whitespace-pre-wrap text-[12px] leading-[1.6] text-fg-soft feed-wrap">
                            {item.detail}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
